import { Flex, Form, Input } from 'antd';
import { Button } from '@/components/button/button';
import { useNavigate, useOutletContext } from 'react-router';
import { useCreateDocument } from '@/hooks/use-create-document/use-create-document';
import { CustomFilters } from '@/components/custom-filters/custom-filters';
import { useCompanySearch } from '@/hooks/use-company-search/use-company-search';
import { useState, useCallback, useEffect } from 'react';
import styles from './create-document.module.scss';
import { useTranslation } from 'react-i18next';

type DocumentsOutletContext = { companiesList: string[] };

export function CreateDocumentPage() {
  const { t } = useTranslation(['documents', 'common']);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { createDocument, isPending } = useCreateDocument();

  const { companiesList } = useOutletContext<DocumentsOutletContext>();
  const [selectedCompany, setSelectedCompany] = useState<string | undefined>(undefined);
  const [companyQuery, setCompanyQuery] = useState<string>('');
  const { companySearchData, companySearchLoading, companySearchError } = useCompanySearch({
    query: companyQuery,
  });

  const onlyCompany = companiesList?.length === 1 ? companiesList[0] : undefined;

  // The company select does not render for a user with one company, and it is the only
  // control that sets this, so without seeding here Create stays disabled forever.
  useEffect(() => {
    if (!onlyCompany) return;
    setSelectedCompany(onlyCompany);
    form.setFieldsValue({ company_name: onlyCompany });
  }, [form, onlyCompany]);

  const nameValue = Form.useWatch('name', form);
  const contentValue = Form.useWatch('content', form);
  const isCreateDisabled =
    isPending ||
    !selectedCompany ||
    !String(nameValue || '').trim() ||
    !String(contentValue || '').trim();

  const handleCompanyChange = useCallback((updated: { company_name?: string }) => {
    const value = updated.company_name;
    setSelectedCompany(value);
    form.setFieldsValue({ company_name: value });
  }, []);

  const handleFinish = (values: { company_name: string; name: string; content: string }) => {
    createDocument(
      { company_name: values.company_name, name: values.name, content: values.content },
      {
        onSuccess: () => {
          navigate('/documents');
        },
      }
    );
  };

  return (
    <Flex vertical gap={16} className={styles.card}>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item name="company_name" hidden>
          <Input />
        </Form.Item>

        {/* Hidden as a pair: one company hides the select, and a lone label over an
            empty gap is worse than neither. */}
        <div className={styles.field} hidden={!!onlyCompany}>
          <label className={styles.fieldLabel} htmlFor="create-document-company">
            {t('documents:create.company')}
          </label>
          <CustomFilters
            id="create-document-company"
            customClassName={undefined}
            companiesList={companiesList || []}
            filters={{ company_name: selectedCompany }}
            defaultCompanyValue={undefined}
            onChangeFilters={handleCompanyChange}
            onSearchQueryChange={setCompanyQuery}
            searchResults={companySearchData}
            isSearchLoading={companySearchLoading}
            searchError={companySearchError as Error | null}
          />
        </div>

        <Form.Item label={t('documents:create.name')} name="name">
          <Input
            placeholder={t('documents:create.namePlaceholder')}
            className={styles.documentNameInput}
          />
        </Form.Item>

        <Form.Item label={t('documents:create.content')} name="content">
          <Input.TextArea
            className={styles.documentContentInput}
            rows={10}
            autoSize={{ minRows: 8, maxRows: 40 }}
            placeholder={t('documents:create.contentPlaceholder')}
          />
        </Form.Item>
      </Form>

      <Flex gap={8} justify="end">
        <Button buttonVariant="secondary" onClick={() => navigate('/documents')}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          buttonVariant="primary"
          loading={isPending}
          disabled={isCreateDisabled}
          onClick={() => form.submit()}
        >
          {t('common:actions.create')}
        </Button>
      </Flex>
    </Flex>
  );
}
